<template>
  <fieldset>
    <div class="field">
      <label class="label">Business Account ID</label>
      <div class="control">
        <input
          class="input"
          type="text"
          placeholder="Enter Business Account ID here"
          required
          v-model.trim="businessAccountId"
          @input="invalidateBusinessAccountIdField()"
          :class="{ 'is-danger': $v.businessAccountId.$invalid && $v.businessAccountId.$dirty }"
          :disabled="hasWhatsAppInstalledApp"
        />
        <p class="help is-danger" v-if="$v.businessAccountId.$invalid && $v.businessAccountId.$dirty">
          The Business Account ID is required
        </p>
      </div>
    </div>

    <div class="field" v-if="!hasWhatsAppInstalledApp">
      <label class="label">API Key</label>
      <div class="control">
        <input
          class="input"
          type="password"
          placeholder="Enter API Key here"
          required
          v-model.trim="apiKey"
          @input="invalidateApiKeyField()"
          :class="{ 'is-danger': $v.apiKey.$invalid && $v.apiKey.$dirty }"
        />
        <p class="help is-danger" v-if="$v.apiKey.$invalid && $v.apiKey.$dirty">The API Key is required</p>
      </div>
    </div>

    <div class="field" v-if="!hasWhatsAppInstalledApp">
      <div class="control">
        <a
          v-if="!isSecretValid(apiKeyValid)"
          class="button is-link"
          @click="validateApiKey"
          :class="{ 'is-loading': isSecretValidating(apiKeyValid) }"
          >Validate</a
        >
        <a v-else disabled class="button is-success">Validated</a>
      </div>
    </div>

    <div class="field">
      <label class="label">Phone Number</label>
      <div
        v-if="!hasWhatsAppInstalledApp"
        class="select is-fullwidth"
        :class="{ 'is-loading': isSecretValidating(apiKeyValid) }"
      >
        <select v-model="selectedPhoneNumber" :disabled="!isSecretValid(apiKeyValid)">
          <option v-for="number in phoneNumbers" :key="number.id" :value="number">{{ number.label }}</option>
        </select>
      </div>
      <p class="help is-danger" v-if="$v.selectedPhoneNumber.$invalid && $v.selectedPhoneNumber.$dirty">
        The Phone Number is required
      </p>

      <div v-if="hasWhatsAppInstalledApp" class="select is-fullwidth">
        <select disabled>
          <option>{{ selectedPhoneNumber?.label }}</option>
        </select>
      </div>
    </div>

    <div class="field">
      <label class="label">Subscribed App</label>
      <div class="control">
        <div class="field">
          <div class="control">
            <input class="input" type="text" disabled v-model="appName" />
          </div>
        </div>
      </div>
    </div>

    <div class="field">
      <label class="label">App ID</label>
      <div class="control">
        <div class="field">
          <div class="control">
            <input class="input" type="text" disabled v-model="appId" />
          </div>
        </div>
      </div>
    </div>

    <div class="field" v-if="!hasWhatsAppInstalledApp">
      <label class="label">App Secret</label>
      <div class="control">
        <input
          class="input"
          type="password"
          :placeholder="isSecretValid(apiKeyValid) ? 'Enter App Secret here' : ''"
          required
          v-model.trim="appSecret"
          @input="invalidateAppSecretField()"
          :class="{ 'is-danger': $v.appSecret.$invalid && $v.appSecret.$dirty }"
          :disabled="!isSecretValid(apiKeyValid)"
        />
        <p class="help is-danger" v-if="$v.appSecret.$invalid && $v.appSecret.$dirty">The App Secret is required</p>
      </div>
    </div>

    <div class="field" v-if="!hasWhatsAppInstalledApp">
      <div class="control">
        <a
          v-if="!isSecretValid(appSecretValid)"
          class="button is-link"
          @click="validateAppSecret"
          :class="{ 'is-loading': isSecretValidating(appSecretValid) }"
          >Validate</a
        >
        <a v-else disabled class="button is-success">Validated</a>
      </div>
    </div>

    <n-button-group>
      <n-button
        variant="primary"
        @click="save"
        :disabled="$v.$invalid || !isSecretValid(apiKeyValid) || !isSecretValid(appSecretValid)"
        :loading="actionInProgress"
        >Submit</n-button
      >
      <n-button @click="cancel" variant="primary--outline">Cancel</n-button>
    </n-button-group>
  </fieldset>
</template>

<script lang="ts" setup>
import { required } from '@vuelidate/validators';
import Toast from '@/shared/services/toast';
import { InstallWhatsAppRequest } from './models';
import { WhatsAppApi, whatsAppApi } from './services/whatsappApi';
import { useStore } from 'vuex';
import { computed, onBeforeMount, ref } from 'vue';
import useVuelidate from '@vuelidate/core';
import { useRouter } from 'vue-router';

interface PhoneNumberSelectOption {
  id: string;
  label: string;
}

enum SecretValidity {
  Invalid = 0,
  Validating = 1,
  Valid = 2,
}

const router = useRouter();
const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);
const currentApp = computed(() => store.getters.currentApp);

const businessAccountId = ref('');
const apiKey = ref('');
const appName = ref('');
const appId = ref('');
const appSecret = ref('');
const phoneNumbers = ref<PhoneNumberSelectOption[]>([]);
const selectedPhoneNumber = ref<PhoneNumberSelectOption | null>();
const apiKeyValid = ref(SecretValidity.Invalid);
const appSecretValid = ref(SecretValidity.Invalid);
const hasWhatsAppInstalledApp = ref(false);
const actionInProgress = ref(false);

const validations = {
  businessAccountId: { required },
  apiKey: { required },
  selectedPhoneNumber: { required },
  appSecret: { required },
};

const $v = useVuelidate(validations, { businessAccountId, apiKey, selectedPhoneNumber, appSecret });

const emit = defineEmits<{
  (e: 'loaded'): void;
  (e: 'configSaved'): void;
}>();

const api: WhatsAppApi = whatsAppApi;

onBeforeMount(() => {
  if (currentApp.value.isInstalled) {
    api
      .getInstalledApp(currentWorkspace.value.id)
      .then((response) => {
        if (response.installedApp) {
          hasWhatsAppInstalledApp.value = true;

          const app = response.installedApp;
          businessAccountId.value = app.businessAccountId;
          selectedPhoneNumber.value = { id: '0', label: app.phoneNumber };
          appName.value = app.appName;
          appId.value = app.appId;
        } else {
          hasWhatsAppInstalledApp.value = false;
        }
      })
      .catch((err) => {
        // if it's not found, then we haven't installed the WhatsApp app fully, so let them fill in the form
        hasWhatsAppInstalledApp.value = false;
        if (err.status !== 404) {
          // show error state...
        }
      })
      .finally(() => {
        emit('loaded');
      });
  } else {
    emit('loaded');
  }
});

function isSecretValid(state: SecretValidity) {
  return state === SecretValidity.Valid;
}

function isSecretValidating(state: SecretValidity) {
  return state === SecretValidity.Validating;
}

function invalidateBusinessAccountIdField() {
  $v.value.businessAccountId.$touch();
  invalidateApiKey();
}

function invalidateApiKeyField() {
  $v.value.apiKey.$touch();
  invalidateApiKey();
}

async function validateApiKey() {
  if (hasWhatsAppInstalledApp.value) {
    return;
  }

  if (businessAccountId.value && apiKey.value) {
    await getWhatsAppAccount();
  } else {
    phoneNumbers.value = [];
    selectedPhoneNumber.value = null;
  }
}

async function getWhatsAppAccount() {
  try {
    apiKeyValid.value = SecretValidity.Validating;
    const response = await api.getAccount(currentWorkspace.value.id, businessAccountId.value, apiKey.value);

    phoneNumbers.value = response.phoneNumbers.map((p) => ({ id: p.id, label: p.number }));

    if (phoneNumbers.value.length > 0) {
      if (!selectedPhoneNumber.value) {
        selectedPhoneNumber.value = phoneNumbers.value[0];
      }

      if (response.subscribedApp) {
        appId.value = response.subscribedApp.appId;
        appName.value = response.subscribedApp.name;
        apiKeyValid.value = SecretValidity.Valid;
        return;
      }
    }
  } catch (e: any) {
    apiKeyValid.value = SecretValidity.Invalid;
    phoneNumbers.value = [];
    selectedPhoneNumber.value = null;
    Toast.showError('Failed to verify API Key');
  }
}

function invalidateAppSecretField() {
  $v.value.appSecret.$touch();
  appSecretValid.value = SecretValidity.Invalid;
}

async function validateAppSecret() {
  if (hasWhatsAppInstalledApp.value) {
    return;
  }

  if (appId.value && appSecret.value) {
    await getAppSubscription();
  }
}

async function getAppSubscription() {
  try {
    appSecretValid.value = SecretValidity.Validating;
    await api.getSubscription(currentWorkspace.value.id, appId.value, appSecret.value);

    appSecretValid.value = SecretValidity.Valid; // doesn't return unauthorized, so we assume it's valid
  } catch (e: any) {
    appSecretValid.value = SecretValidity.Invalid;
    Toast.showError('Failed to verify App Secret');
  }
}

function save() {
  if (actionInProgress.value) {
    return;
  }

  actionInProgress.value = true;

  const request: InstallWhatsAppRequest = {
    installId: currentApp.value.installedAppId,
    workspaceId: currentWorkspace.value.id,
    businessAccountId: businessAccountId.value,
    apiKey: apiKey.value,
    phoneNumberId: selectedPhoneNumber.value!.id,
    phoneNumber: selectedPhoneNumber.value!.label,
    appName: appName.value,
    appId: appId.value,
    appSecret: appSecret.value,
  };

  api
    .install(request)
    .then(() => emit('configSaved'))
    .catch((err) => Toast.showError(err.message))
    .finally(() => (actionInProgress.value = false));
}

function cancel() {
  router.push({ name: 'apps' });
}

function invalidateApiKey() {
  apiKeyValid.value = SecretValidity.Invalid;
  appSecretValid.value = SecretValidity.Invalid;
  selectedPhoneNumber.value = null;
  appName.value = '';
  appId.value = '';
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.action-group {
  margin-top: 50px;
  margin-bottom: 20px;
}
</style>
